import { useDraggable } from '@dnd-kit/core'
import { Check, GripVertical, Plus, Search } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Input } from '@/components/ui/input'
import { useAuth } from '@/lib/auth/AuthProvider'
import { cn } from '@/lib/utils'
import { WIDGET_TYPES, type WidgetType } from '../types'
import { WIDGET_META } from '../widgets/meta'
import { WIDGETS } from '../widgets/registry'
import { TONE_STYLES } from '../widgets/tones'
import { LIBRARY_PREFIX, type LibraryDragData } from './useEditorDnd'

export function LibraryCard({ type, dragging, grip }: { type: WidgetType; dragging?: boolean; grip?: boolean }) {
  const { t } = useTranslation()
  const meta = WIDGET_META[type]
  const Icon = WIDGETS[type].icon
  return (
    <div
      className={cn(
        'flex min-w-0 flex-1 items-center gap-3 rounded-xl border bg-card p-2.5 text-left',
        dragging && 'w-64 rotate-1 border-primary/40 shadow-[0_12px_28px_-8px_color-mix(in_oklab,var(--shadow-color)_35%,transparent)]',
      )}
    >
      <span className={cn('flex size-9 shrink-0 items-center justify-center rounded-lg ring-1 ring-inset', TONE_STYLES[meta.tone].icon)}>
        <Icon className="size-4" aria-hidden />
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-1.5">
          <span className="truncate text-[13px] font-semibold">{t(`dashboard.widgets.${type}.title`)}</span>
          {meta.restricted ? (
            <span className="shrink-0 rounded-full bg-warning/15 px-1.5 py-px text-[10px] font-medium text-warning-strong">
              {t('dashboard.edit.restricted')}
            </span>
          ) : null}
        </span>
        <span className="block truncate text-[11px] text-muted-foreground">{t(`dashboard.widgets.${type}.description`)}</span>
      </span>
      {grip || dragging ? <GripVertical className="size-4 shrink-0 text-muted-foreground/60" aria-hidden /> : null}
    </div>
  )
}

function LibraryItem({ type, added, onAdd }: { type: WidgetType; added: boolean; onAdd: () => void }) {
  const { t } = useTranslation()
  const title = t(`dashboard.widgets.${type}.title`)
  const data: LibraryDragData = { source: 'library', type }
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: `${LIBRARY_PREFIX}${type}`, data, disabled: added })

  return (
    <li className={cn('group/lib relative flex items-center gap-1', isDragging && 'opacity-40', added && 'opacity-60')}>
      <div
        ref={setNodeRef}
        {...attributes}
        {...listeners}
        aria-label={t('dashboard.edit.dragFromLibrary', { title })}
        className={cn(
          'flex min-w-0 flex-1 items-center rounded-xl outline-none transition-shadow focus-visible:ring-2 focus-visible:ring-ring',
          added ? 'cursor-default' : 'cursor-grab hover:shadow-sm active:cursor-grabbing',
        )}
      >
        <LibraryCard type={type} grip={!added} />
      </div>
      <button
        type="button"
        onClick={onAdd}
        disabled={added}
        aria-label={added ? t('dashboard.edit.alreadyAdded', { title }) : t('dashboard.edit.addWidget', { title })}
        className={cn(
          'flex size-8 shrink-0 items-center justify-center rounded-lg border transition-colors',
          added ? 'border-success/30 bg-success/10 text-success' : 'bg-card text-muted-foreground hover:border-primary/40 hover:text-primary',
        )}
      >
        {added ? <Check className="size-4" /> : <Plus className="size-4" />}
      </button>
    </li>
  )
}

export function WidgetLibrary({ addedTypes, onAdd }: { addedTypes: Set<WidgetType>; onAdd: (type: WidgetType) => void }) {
  const { t } = useTranslation()
  const { can } = useAuth()
  const [query, setQuery] = useState('')
  const q = query.trim().toLocaleLowerCase('tr')
  const available = WIDGET_TYPES.filter((type) => can(WIDGET_META[type].perm))
  const visible = available.filter((type) =>
    `${t(`dashboard.widgets.${type}.title`)} ${t(`dashboard.widgets.${type}.description`)}`.toLocaleLowerCase('tr').includes(q),
  )

  return (
    <aside className="flex max-h-80 min-h-0 flex-col rounded-2xl border bg-card p-3 lg:sticky lg:top-3 lg:max-h-[calc(100dvh-7rem)]">
      <h2 className="px-1 text-sm font-semibold">{t('dashboard.edit.library')}</h2>
      <div className="relative mt-3">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('dashboard.edit.searchWidgets')}
          aria-label={t('dashboard.edit.searchWidgets')}
          className="h-9 pl-8"
        />
      </div>
      <div className="mt-4 flex items-center justify-between px-1">
        <p className="text-xs font-semibold text-muted-foreground">{t('dashboard.edit.availableModules')}</p>
        <span className="text-[11px] tabular-nums text-muted-foreground">
          {addedTypes.size}/{available.length}
        </span>
      </div>
      <ul className="-mx-1 mt-2 min-h-0 flex-1 space-y-2 overflow-y-auto scrollbar-none px-1 pb-1">
        {visible.map((type) => (
          <LibraryItem key={type} type={type} added={addedTypes.has(type)} onAdd={() => onAdd(type)} />
        ))}
        {visible.length === 0 ? <li className="py-6 text-center text-xs text-muted-foreground">{t('dashboard.edit.noWidgetMatch')}</li> : null}
      </ul>
      <p className="mt-3 rounded-lg bg-muted/50 px-2.5 py-2 text-[11px] leading-relaxed text-muted-foreground">{t('dashboard.edit.libraryHint')}</p>
    </aside>
  )
}
